import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { CheckmarkIcon, FlexGap, IconWrapper, Paragraph, Spinner, tokens } from '@treams/treams-ui';

const LoadingStateWrapper = styled(FlexGap)<{ $completed: boolean }>`
	opacity: ${({ $completed }) => ($completed ? 0.5 : 1)};
	transition: opacity 0.3s ease-in-out;
`;

const SpinnerBox = styled(Box)<{ $showIcon: boolean }>`
	transition: width 0.3s ease-in-out;
	width: ${({ $showIcon }) => ($showIcon ? '20px' : '0px')};
	overflow: hidden;
	flex-shrink: 0;
`;

export const EvaluationAnswerAIInsightsLoadingState = () => {
	const { t } = useTranslation();

	const loadingStates = [
		t('evaluation.submission.aiInsights.loading.notes'),
		t('evaluation.submission.aiInsights.loading.goals'),
		t('evaluation.submission.aiInsights.loading.conversations'),
		t('evaluation.submission.aiInsights.loading.feedback'),
	];

	const [currentState, setCurrentState] = useState(0);

	const getRandomDelay = useCallback(() => Math.floor(Math.random() * 3000) + 2000, []);

	useEffect(() => {
		if (currentState + 1 === loadingStates.length) return;

		const timeout = setTimeout(() => {
			setCurrentState((currentState) => currentState + 1);
		}, getRandomDelay());

		return () => clearTimeout(timeout);
	}, [loadingStates.length, currentState, getRandomDelay]);

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.element}>
			{loadingStates.map((state, index) => {
				const isCompleted = index < currentState;
				const isCurrent = index === currentState;

				return (
					<LoadingStateWrapper
						key={`${state}-${index}`}
						alignItems="center"
						gap={tokens.gap.inline}
						$completed={isCompleted}
					>
						<SpinnerBox $showIcon={isCurrent || isCompleted}>
							{isCompleted && (
								<IconWrapper size={16} color={tokens.brand.icon}>
									<CheckmarkIcon />
								</IconWrapper>
							)}
							{isCurrent && <Spinner />}
						</SpinnerBox>
						<Paragraph fontWeight={isCurrent ? 'medium' : undefined}>{state}</Paragraph>
					</LoadingStateWrapper>
				);
			})}
		</FlexGap>
	);
};
